import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Lock, Eye, EyeOff } from "lucide-react";

interface ImportPasswordProtectionProps {
  children: React.ReactNode;
  isLimitedUser: boolean;
}

export function ImportPasswordProtection({ children, isLimitedUser }: ImportPasswordProtectionProps) {
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { toast } = useToast();

  const CORRECT_PASSWORD = "windranger0111";

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setIsPasswordDialogOpen(false);
      setPassword("");
      toast({
        title: "Access Granted",
        description: "You can now use the import functionality",
      });
    } else {
      toast({
        title: "Access Denied",
        description: "Incorrect password. Please try again.",
        variant: "destructive"
      });
      setPassword("");
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (!isLimitedUser) {
      // Full access users can use import directly
      return;
    }
    
    if (isAuthenticated) {
      // Already authenticated, allow access
      return;
    }
    
    // Block the click and show password dialog for limited users
    e.preventDefault();
    e.stopPropagation();
    setIsPasswordDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // When dialog is closed, reset password but keep authentication status
      setPassword("");
    }
    setIsPasswordDialogOpen(open);
  };

  // For limited users who haven't authenticated, show a blocked version
  if (isLimitedUser && !isAuthenticated) {
    return (
      <>
        <div onClick={handleTriggerClick} style={{ cursor: 'pointer' }}>
          {children}
        </div>
        
        <Dialog open={isPasswordDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-orange-600" />
                Import Access Required
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  This feature requires special access. Please enter the import password to continue.
                </p>
              </div>
              
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="import-password">Import Password</Label>
                  <div className="relative">
                    <Input
                      id="import-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter import password"
                      className="pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDialogClose(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    Unlock Import
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // For full access users or authenticated limited users, render children normally
  return <>{children}</>;
}
